import HBButton from "@/components/HBButton";
import HBIcon from "@/components/HBIcon";
import {
  PinDropdown,
  useLastBoards,
} from "@/components/PinCreator/PinDropdown";
import { UserGuideTooltip } from "@/components/UserGuideTooltip";
import { PRIVATETYPE } from "@/constants/board";
import { EVENT } from "@/constants/event";
import {
  SELECT_BOARD_PIN_COUNT_THRESHOLD,
  USER_BOARD_RECOMMEND_THRESHOLD,
} from "@/constants/pinCollectThreshold";
import { SHORTCUT } from "@/constants/shortcut";
import useAppStore from "@/context/store";
import { button_click as buttonClickTracker } from "@/tracker/collecter/buttonClick";
import { createPinSuccessAnimation } from "@/utils/animations";
import { getContentType } from "@/utils/getContentType";
import { usePinCreate } from "@/utils/hooks/usePinCreate";
import { useRealtimePinCount } from "@/utils/hooks/useRealtimePinCount";
import imageUrl from "@/utils/imageUrl";
import isEditingElement from "@/utils/isEditingElement";
import useOuterClick from "@/utils/useOuterClick";
import useShortcut from "@/utils/useShortcut";
import { useMemoizedFn } from "ahooks";
import { Button, Dropdown } from "antd";
import classNames from "classnames";
import { useRouter } from "next/router";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./index.module.less";

// eslint-disable-next-line no-unused-vars
// let hasCurrentPin = false;
/**
 *
 * @param {{
 *  pin: IPin,
 *  onOk?: (pin: IPin) => void,
 *  current: boolean,
 *  shortcutOrder?: number,
 *  className?: string,
 *  isPinDetail?: boolean,
 *  hasPinCreated?: boolean,
 *  searchWord?: string,
 *  boardMatchingResult?: any,
 *  showTooltip?: boolean,
 *  onUserBoardUpdate?: (board: any) => void,
 *  isNewUser?: boolean,
 *  moduleName?: string,
 *  relatedBoards?: any[], // 外部传入的同在画板数据
 *  currentBoard?: any,
 *  enableCurrentBoardPriority?: boolean,
 *  needAnimation?: boolean,
 *  enableBoardRecommend?: boolean, // 是否启用画板推荐功能
 * }} props
 * @param {*} actionRef
 * @returns
 */
function PurePinButton(
  {
    pin,
    onOk,
    current,
    shortcutOrder = 1,
    className,
    isPinDetail,
    showTooltip,
    hasPinCreated,
    searchWord,
    boardMatchingResult,
    onUserBoardUpdate,
    isNewUser = false,
    moduleName,
    relatedBoards: _relatedBoards,
    currentBoard, // 当前画板信息，用于画板选择器默认值
    enableCurrentBoardPriority, // 是否启用当前画板优先逻辑
    needAnimation,
    enableBoardRecommend = true, // 默认启用画板推荐功能（新版）
  },
  actionRef
) {
  const [ref, visible, setVisible] = useOuterClick(false);
  const [board, setBoard] = useState(null);
  const [type, setType] = useState("flashTip");
  const [isMatching, setIsMatching] = useState(false);
  const router = useRouter();
  const { user, userReady } = useAppStore(({ user, userReady }) => ({
    user,
    userReady,
  }));
  const pinitTimeoutRef = useRef(false);

  const { lastBoards, updateUnclassifiedBoardUpdateTime, hideBoardSelect } =
    useLastBoards(currentBoard, enableCurrentBoardPriority);

  const incrementPinCount = useAppStore((state) => state.incrementPinCount);
  const incrementPinDelta = useAppStore.getState()?.incrementPinDelta;

  // 使用实时用户采集数
  const pinCount = useRealtimePinCount();

  // 保存已采集的 pin 信息，用于移动画板
  const [createdPinForMove, setCreatedPinForMove] = useState(null);

  // 获取用户画板信息用于判断新用户
  const { lastBoards: userBoards, lastBoardsReady } = useAppStore(
    ({ lastBoards, lastBoardsReady }) => ({
      lastBoards,
      lastBoardsReady,
    })
  );

  const {
    pinitModalRef,
    onFlash,
    onCreate,
    sucessCallback,
    isCreated,
    onUndo,
  } = usePinCreate({
    lastBoards,
    pin,
    hasPinCreated,
    boardMatchingResult,
    enableBoardRecommend,
    onPinSuccess: (pin) => {
      setDropdownVisible(false);
      setVisible(false);
      // 采集成功后增加本地增量与兼容旧的 pinCount
      incrementPinCount && incrementPinCount();
      incrementPinDelta && incrementPinDelta();
      onOk?.(pin);
      if (pin?.board?.is_private === PRIVATETYPE.notClass) {
        updateUnclassifiedBoardUpdateTime();
      }
      needAnimation &&
        createPinSuccessAnimation(
          imageUrl(pin?.file, { width: 180, height: 180, rect: "sq" })
        );

      // 检查是否为新用户无画板第一次采集，且采集到了"未整理的采集"画板
      const isUnclassifiedBoard =
        pin?.board?.board_id === 0 || pin?.board?.title === "未整理的采集";
      // 第一次采集：采集前 pinCount 为 0，采集后（调用 incrementPinDelta 后）应该为 1
      // 由于 zustand 状态更新是同步的，我们可以直接从 store 获取最新的 deltaPinCount
      const currentDeltaPinCount = useAppStore.getState().deltaPinCount || 0;
      const currentBasePinCount = useAppStore.getState().basePinCount || 0;
      const currentPinCount = currentBasePinCount + currentDeltaPinCount;
      const isNewUserFirstCollect =
        lastBoardsReady && userBoards.length === 0 && currentPinCount === 1;

      if (
        enableBoardRecommend &&
        isUnclassifiedBoard &&
        isNewUserFirstCollect
      ) {
        // 保存已采集的 pin 信息，用于后续移动
        setCreatedPinForMove(pin);
        // 延迟打开弹窗，确保采集成功消息已显示
        setTimeout(() => {
          setDropdownVisible(true);
        }, 300);
      }
    },
    onPinWarn: (result) => {
      setBoard(result.pin?.board);
      setType("confirm");
      setVisible(true);
    },
  });

  // 监听匹配状态变化
  useEffect(() => {
    // 老版本不显示匹配状态
    if (!enableBoardRecommend) {
      setIsMatching(false);
      return;
    }

    if (boardMatchingResult !== null) {
      setIsMatching(false);
    } else if (pin?.pin_id) {
      setIsMatching(true);
    }
  }, [enableBoardRecommend, boardMatchingResult, pin?.pin_id]);

  const onCancel = useCallback(() => {
    setVisible(false);
  }, [setVisible]);

  const onFlashEnter = useCallback(() => {
    if (!visible) {
      setType("flashTip");
      setVisible(true);
    }
  }, [setVisible, visible]);

  const onFlashLeave = useCallback(() => {
    if (type === "flashTip") {
      setVisible(false);
    }
  }, [setVisible, type]);

  // 快捷键：采集 和 快采
  useShortcut(
    SHORTCUT.PIN,
    (e) => {
      setVisible(false);
      // 关注页面需要记录用户有没有操作
      if (router.pathname === "/follow") {
        window.dispatchEvent(new Event(EVENT.CARD_OPERATE));
      }
      if (e.ctrlKey || e.metaKey) {
        return false;
      }
      const editing = isEditingElement();
      if (editing) {
        return false;
      }

      const quickTime = 300; // 双击判断时长 ms

      if (pinitModalRef.current) {
        pinitModalRef.current.destroy();
        pinitModalRef.current = null;
        return false;
      }

      // 当前
      if (current) {
        const pinitTimeout = pinitTimeoutRef.current;

        // 快采
        if (pinitTimeout) {
          clearTimeout(pinitTimeout);

          // 1. 先设置为中间态 0
          pinitTimeoutRef.current = 0;

          // 2.再延时设置为 false
          setTimeout(() => {
            pinitTimeoutRef.current = false;
          }, quickTime);

          // 埋点上报
          buttonClickTracker(flashPinBtnRef.current, {
            other: {
              button_name: "快捷键快采",
            },
          });

          onFlash(true);
          return false;
        }

        if (pinitTimeout === 0) {
          return false;
        }

        // 转采
        pinitTimeoutRef.current = setTimeout(() => {
          setDropdownVisible(true);

          // 埋点上报
          // 埋点上报
          buttonClickTracker(dropdownRef.current || pinBtnRef.current, {
            other: {
              button_name: "快捷键采集",
            },
          });

          // 1. 先设置为中间态 0
          pinitTimeoutRef.current = 0;

          // 2.再延时设置为 false
          setTimeout(() => {
            pinitTimeoutRef.current = false;
          }, quickTime);
        }, quickTime);

        return false;
      }
    },
    { order: shortcutOrder }
  );

  useImperativeHandle(actionRef, () => {
    return {
      create: onCreate,
      flash: onFlash,
    };
  });

  const overlay = useMemo(() => {
    if (type === "confirm") {
      return (
        <div className={styles.modal} ref={ref}>
          <div className={styles.content}>
            <span>该图片已在画板</span>
            <span className={styles.bold}>{board?.title}</span>
            <span>，要再次采集？</span>
          </div>
          <div className={styles.footer}>
            <Button
              className={styles.button}
              size="small"
              onClick={onCancel}
              style={{ borderRadius: 100, height: 36 }}
            >
              取消
            </Button>
            <HBButton
              className={styles.button}
              type="primary"
              size="small"
              onClick={() => onFlash(false)}
            >
              采集
            </HBButton>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.tooltip} ref={ref}>
        <span>
          {isMatching
            ? "快采到：···"
            : boardMatchingResult?.recommendedBoard?.title
            ? `快采到：${boardMatchingResult.recommendedBoard.title}`
            : lastBoards?.length > 0 && lastBoards[0]?.title
            ? `快采到：${lastBoards[0]?.title}`
            : "快采到：未整理的采集"}
        </span>
      </div>
    );
  }, [
    type,
    ref,
    lastBoards,
    board?.title,
    onCancel,
    onFlash,
    isMatching,
    boardMatchingResult?.recommendedBoard?.title,
  ]);

  const pinBtnRef = useRef();
  const flashPinBtnRef = useRef();

  const dropdownContentRef = useRef();
  const [dropdownRef, dropdownVisible, setDropdownVisible] = useOuterClick(
    false,
    { extraTargets: [dropdownContentRef] }
  );
  const [dropdownPlacement, setDropdownPlacement] = useState("bottomLeft");

  const onPinClick = useMemoizedFn(() => {
    const btn = dropdownRef.current;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight;

    // 判断是否在可视范围内
    if (
      rect.bottom + 430 > windowHeight &&
      windowHeight - rect.bottom < rect.top
    ) {
      setDropdownPlacement("topLeft");
    } else {
      setDropdownPlacement("bottomLeft");
    }

    setDropdownVisible(!dropdownVisible);
  });

  const isPinCreated = isCreated || hasPinCreated;

  // 老版本：获取第一个非"未整理的采集"的画板
  const getFirstUserCreatedBoard = () => {
    if (!lastBoards || lastBoards.length === 0) return null;
    // 过滤掉 board_id === 0 的"未整理的采集"
    const userBoards = lastBoards.filter((board) => board?.board_id !== 0);
    return userBoards.length > 0 ? userBoards[0] : null;
  };

  const firstUserCreatedBoard = getFirstUserCreatedBoard();

  // 根据 AB 测试决定标题显示逻辑
  const matchTitle = isMatching
    ? lastBoards?.length >= USER_BOARD_RECOMMEND_THRESHOLD
      ? lastBoards[0]?.title
      : "···"
    : enableBoardRecommend
    ? // 新版本：精确控制
      pinCount === SELECT_BOARD_PIN_COUNT_THRESHOLD && !firstUserCreatedBoard
      ? // 第4个采集：显示选择画板；若已采集则显示未整理的采集
        isPinCreated
        ? "未整理的采集"
        : "选择画板"
      : isNewUser
      ? "选择画板"
      : // 当用户画板数 > 5 时，只显示采集至画板
      lastBoards?.length >= USER_BOARD_RECOMMEND_THRESHOLD
      ? lastBoards[0]?.title ?? "未整理的采集"
      : boardMatchingResult?.recommendedBoard?.title ??
        lastBoards[0]?.title ??
        "未整理的采集"
    : // 老版本：只显示用户创建的画板，排除"未整理的采集"
      firstUserCreatedBoard?.title ?? "采集";

  // 检查是否为新用户第一次采集（用于强制显示弹窗）
  // 注意：这里使用 pinCount === 1 来判断，因为采集成功后 pinCount 已经增加了
  const isNewUserFirstCollectForShow =
    lastBoardsReady &&
    userBoards.length === 0 &&
    pinCount === 1 &&
    createdPinForMove !== null;

  // 根据 AB 测试重新计算是否隐藏画板选择
  const shouldHideBoardSelect = !user
    ? // 未登录：隐藏画板选择器
      true
    : enableBoardRecommend
    ? // 新版本：前3个强制隐藏；第4个强制显示；其余按原有逻辑
      // 详情页强制显示
      isPinDetail
      ? false
      : // 新用户第一次采集时强制显示（用于自动弹窗引导）
      isNewUserFirstCollectForShow
      ? false
      : pinCount < SELECT_BOARD_PIN_COUNT_THRESHOLD && !firstUserCreatedBoard
      ? true
      : pinCount === SELECT_BOARD_PIN_COUNT_THRESHOLD && !firstUserCreatedBoard
      ? false
      : hideBoardSelect &&
        !isNewUser &&
        !(
          lastBoards?.length === 0 &&
          boardMatchingResult?.recommendedRelatedBoards?.length > 0
        )
    : // 老版本：新用户或只有未整理画板时隐藏选择画板
      isNewUser || !firstUserCreatedBoard;

  return (
    <div
      className={classNames(
        styles.pinActions,
        styles.isNew,
        className,
        isPinDetail && styles.pinDetail
      )}
    >
      {!shouldHideBoardSelect ? (
        <PinDropdown
          pin={pin}
          onOk={sucessCallback}
          visible={dropdownVisible}
          contentRef={dropdownContentRef}
          placement={dropdownPlacement}
          searchWord={searchWord}
          boardMatchingResult={boardMatchingResult}
          onUserBoardUpdate={onUserBoardUpdate}
          currentBoard={currentBoard}
          enableCurrentBoardPriority={enableCurrentBoardPriority}
          enableBoardRecommend={enableBoardRecommend}
          createdPinForMove={createdPinForMove}
          onMoveComplete={() => {
            setCreatedPinForMove(null);
            setDropdownVisible(false);
          }}
        >
          <div
            ref={dropdownRef}
            className={classNames(
              styles.pinBtn,
              dropdownVisible && styles.active
            )}
            onClick={onPinClick}
            data-gd-click="button_click"
            data-button-name="选择画板"
            data-content-type={getContentType(pin?.extra)}
            data-content-id={pin?.pin_id}
            data-board-id={pin?.board_id}
            data-file-id={pin?.file_id}
            data-module-name={moduleName}
            data-search-word={searchWord}
            data-ext={JSON.stringify(pin?.ext)}
            title={current ? "选择画板 [R]" : ""}
          >
            <span className={styles.text} title={matchTitle}>
              {matchTitle}
            </span>
            <HBIcon type="ic_arrow_down" />
          </div>
        </PinDropdown>
      ) : (
        <div></div>
      )}

      <UserGuideTooltip
        needUserId={false}
        visible={userReady && !user && showTooltip}
        title={
          <div className={styles.tipContainer}>
            <div className={styles.tipTitle}>点采集，可以收藏图片哦</div>
            <div>
              创建你的在线灵感图库，创作时候能随时随地翻看，提高创作效率
            </div>
          </div>
        }
        containerClassName={styles.tipContent}
        localStorageKey={() => "hb_new_user_pin_btn_tooltip"}
        placement="leftTop"
      >
        <div
          className={classNames(
            styles.flashBtn,
            isPinCreated ? styles.active : null
          )}
        >
          <Dropdown
            placement="bottomCenter"
            visible={isPinCreated || type !== "confirm" ? false : visible}
            overlay={overlay}
            destroyPopupOnHide
          >
            <HBButton
              type="primary"
              className={styles.pick}
              onClick={() => {
                if (isPinCreated) {
                  onUndo(pin);
                } else if (isNewUser) {
                  if (enableBoardRecommend) {
                    // 新版本：新用户点击时显示选择画板弹窗
                    setDropdownVisible(true);
                  } else {
                    // 老版本：新用户直接快采到未整理
                    onFlash(true);
                  }
                } else {
                  onFlash(true);
                }
              }}
              onMouseEnter={onFlashEnter}
              onMouseLeave={onFlashLeave}
              data-gd-click="button_click"
              data-button-name={
                isPinCreated
                  ? "撤销采集"
                  : isNewUser
                  ? enableBoardRecommend
                    ? "选择画板"
                    : "快采"
                  : "快采"
              }
              data-content-id={pin?.pin_id}
              data-board-id={pin?.board_id}
              data-file-id={pin?.file_id}
              data-material-id={
                pin?.file_material?.material_id || pin?.extra?.material_id
              }
              data-content-type={getContentType(pin)}
              data-module-name={moduleName}
              data-search-word={searchWord}
              data-ext={JSON.stringify(pin?.ext)}
              title={current ? (isPinCreated ? "撤销采集" : "") : ""}
              ref={flashPinBtnRef}
              active={isPinCreated}
            >
              {isPinCreated ? "已采集" : "采集"}
            </HBButton>
          </Dropdown>
        </div>
      </UserGuideTooltip>
    </div>
  );
}

export default forwardRef(PurePinButton);
